export type MensagemStatus = 'PENDING' | 'SENT' | 'DELIVERED' | 'SEEN' | 'FAILED';
export type MensagemDirecao = 'INBOUND' | 'OUTBOUND';
export type MensagemCanal = 'WHATSAPP' | 'EMAIL' | 'SMS';
export type MensagemMidiaTipo = 'AUDIO' | 'IMAGE' | 'DOCUMENT' | 'VIDEO';
/**
 * `SYSTEM` é nota interna (ex.: "Conversa transferida para atendimento humano"): o back grava na
 * mesma conversa, mas ela nunca vai à contraparte e não é mensagem da negociação.
 */
export type MensagemTipo = 'TEXT' | MensagemMidiaTipo | 'SYSTEM';

export interface Mensagem {
  id: string;
  empresaId: string;
  escritorioId: string;
  conversaId: string;
  direction: MensagemDirecao;
  canal: MensagemCanal;
  tipo: MensagemTipo;
  textBody: string | null;
  mediaUrl: string | null;
  providerMessageId: string | null;
  status: MensagemStatus;
  errorMessage: string | null;
  metadata: Record<string, unknown> | null;
  /** Usuário que enviou; `null` quando quem enviou foi a Lexi. */
  senderId?: string | null;
  senderNome?: string | null;
  createdAt: string;
  updatedAt: string;
}

export type ConversaMode = 'BOT' | 'HUMAN';
export type ConversaStatus = 'OPEN' | 'ACTIVE' | 'CLOSED' | 'ARCHIVED';

export interface Conversa {
  id: string;
  empresaId: string;
  escritorioId: string;
  canal: MensagemCanal;
  telefoneNormalizado: string;
  mode: ConversaMode;
  status: ConversaStatus;
  botFlowId: string | null;
  lastMessageAt: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
}

export interface NegociacaoCasoEvent {
  id: string;
  empresaId: string;
  escritorioId?: string;
  entityType: 'negociacaoCaso';
  status: string;
  tipoEncerramento?: string | null;
}

export interface DirectionPendencia {
  id: string;
  negociacaoCasoId: string;
  type: string;
  status: string;
  blocking: boolean;
  title: string | null;
  payload: unknown;
}

export interface DirectionPendenciaEvent {
  id: string;
  empresaId: string;
  escritorioId?: string;
  conversaId?: string;
  kind: 'direction_pendencia_created';
  pendencia: DirectionPendencia;
}

export interface DirectionResolutionEvent {
  id: string;
  empresaId: string;
  escritorioId?: string;
  conversaId?: string;
  kind: 'direction_resolved' | 'direction_failed';
  pendenciaId: string;
  negociacaoCasoId: string;
}

export interface MessagingEvent {
  version: number;
  occurredAt: string;
  data:
    | Mensagem
    | Conversa
    | NegociacaoCasoEvent
    | DirectionPendenciaEvent
    | DirectionResolutionEvent;
}

export interface MessagingContext {
  empresaId: string;
  escritorioId: string;
}

export type AttachmentUploadStatus = 'idle' | 'uploading' | 'sending' | 'error';

export type DocumentoOrigem = 'ESCRITORIO' | 'CONTRAPARTE';

export interface NegociacaoAttachmentUploadResult {
  mediaUrl: string;
  storagePath: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
}

export interface AcceptedAttachmentType {
  mime: string;
  ext: string;
  maxBytes: number;
}

export const ACCEPTED_ATTACHMENT_TYPES: AcceptedAttachmentType[] = [
  { mime: 'application/pdf', ext: '.pdf', maxBytes: 16 * 1024 * 1024 },
  { mime: 'image/jpeg', ext: '.jpg', maxBytes: 5 * 1024 * 1024 },
  { mime: 'image/png', ext: '.png', maxBytes: 5 * 1024 * 1024 },
  {
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ext: '.docx',
    maxBytes: 16 * 1024 * 1024,
  },
];

export const findAcceptedAttachmentType = (mime: string): AcceptedAttachmentType | null =>
  ACCEPTED_ATTACHMENT_TYPES.find((type) => type.mime === mime) ?? null;
